import Image from "next/legacy/image";
import { blurDataURL } from "../lib/constants";
import Meta from "../components/meta";
import { getSettingsForPage } from "../lib/api";
import Link from "next/link";

const CamCard = ({ data }) => {
  // Check if this is a grouped camera project
  const hasMultipleCameras = data?.cameras && data.cameras.length > 0;
  // If it has multiple cameras, use the first camera's data for the card
  const primaryCamera = hasMultipleCameras ? data.cameras[0] : data;

  let href = primaryCamera?.key
    ? `/cams/${primaryCamera.key.toLowerCase()}`
    : "#";
  if (primaryCamera?.status === "completed" && primaryCamera?.projectUrl) {
    href = primaryCamera.projectUrl;
  }
  const isExternal = !!primaryCamera?.url;
  const linkHref = primaryCamera?.url || href;

  return (
    <div className="group relative">
      <Link
        href={linkHref}
        target={isExternal ? "_blank" : undefined}
        className="block"
      >
        <div className="group block w-full aspect-w-10 aspect-h-7 rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-offset-2 transition focus-within:ring-offset-gray-100 focus-within:ring-accent-500 overflow-hidden">
          <Image
            draggable={false}
            className="object-cover pointer-events-none sm:group-hover:opacity-75 transition-opacity"
            alt=""
            layout="fill"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
            placeholder="blur"
            blurDataURL={blurDataURL}
            src={primaryCamera?.img || "/placeholder.svg"}
          />
          <button type="button" className="absolute inset-0 focus:outline-none">
            <span className="sr-only">View details for {data?.name}</span>
          </button>
        </div>
        <p className="mt-2 block text-sm sm:text-base font-medium text-gray-900 pointer-events-none">
          {data?.name}
        </p>
      </Link>

      {/* Camera badges */}
      {hasMultipleCameras && data.cameras.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {data.cameras.map((camera) => {
            if (!camera) return null;

            // Generate link for this specific camera
            let camHref = camera?.key
              ? `/cams/${camera.key.toLowerCase()}`
              : "#";
            if (camera?.status === "completed" && camera?.projectUrl) {
              camHref = camera.projectUrl;
            }
            const camIsExternal = !!camera?.url;
            const camLinkHref = camera?.url || camHref;

            // Determine if this is the primary camera (first one)
            const isPrimary = camera?.key === primaryCamera?.key;

            return (
              <Link
                key={camera?.key || `cam-${Math.random()}`}
                href={camLinkHref}
                target={camIsExternal ? "_blank" : undefined}
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isPrimary
                    ? "bg-gray-100 text-gray-800 hover:bg-gray-200"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
              >
                {camera?.cameraNumber !== null
                  ? `Cam ${camera.cameraNumber}`
                  : camera?.name?.includes("CAM")
                  ? camera.name.match(/CAM\s*(\d+)/i)?.[0] || "Camera"
                  : "Camera"}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function LiveCams({
  activeProjects,
  completedProjects,
  settings,
}) {
  return (
    <>
      <Meta path="/live-cams" {...settings.seo} />

      <div className="max-w-2xl mx-auto px-4 pt-10 pb-6 sm:px-6 lg:max-w-7xl lg:px-8">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 mb-6">
            Live Cams
          </h1>
          <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-5 lg:grid-cols-3 xl:grid-cols-4 mb-16">
            {activeProjects.map((cam) => (
              <CamCard
                key={
                  cam?.cameras && cam?.cameras?.length > 0
                    ? cam?.cameras[0]?.key
                    : cam?.name
                }
                data={cam}
              />
            ))}
          </div>
        </div>

        {completedProjects?.length ? (
          <div>
            <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 mb-6">
              Completed Projects
            </h2>
            <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-5 lg:grid-cols-3 xl:grid-cols-4 mb-12">
              {completedProjects.map((cam) => (
                <CamCard
                  key={
                    cam?.cameras && cam?.cameras?.length > 0
                      ? cam?.cameras[0]?.key
                      : cam?.name
                  }
                  data={cam}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

// eslint-disable-next-line no-unused-vars
export async function getStaticProps({ preview = false }) {
  try {
    const response = await fetch("https://cs-cams.firebaseio.com/list.json", {
      cache: "no-cache",
    });
    const data = await response.json();
    const projects = Object.entries(data || {}).map(([a, b]) => ({
      key: a,
      ...b,
    }));
    let activeProjects = projects.filter(
      (a) => a?.status === "active" || a?.status === "third-party-active"
    );

    let completedProjects = projects.filter(
      (a) => a?.status === "completed-accessible"
    );
    const settings = await getSettingsForPage("cams");

    // Loop through the completed projects and add the projectUrl
    for (let i = 0; i < completedProjects.length; i++) {
      const project = completedProjects[i];
      if (!project?.key) continue;

      try {
        const projectResponse = await fetch(
          `https://cs-cams.firebaseio.com/projects/${project.key.toUpperCase()}/options.json`
        );
        const projectData = await projectResponse.json();
        if (projectData?.projectUrl) {
          completedProjects[i].projectUrl = projectData.projectUrl;
        }
      } catch (error) {
        console.error(`Error fetching project URL for ${project.key}:`, error);
      }
    }

    // Function to group cameras that belong to the same project
    const groupCameras = (projects) => {
      const groupedProjects = {};

      // Process each project
      projects.forEach((project) => {
        if (!project?.name) return;

        // Extract camera number using regex - handle various formats like "CAM1", "CAM 1", "- CAM 1", etc.
        const camMatch = project.name.match(/\s*[-\s]*\s*CAM\s*(\d+)\s*$/i);
        let baseName = project.name;
        let cameraNumber = null;

        if (camMatch) {
          // Remove the CAM X part from the name to get the base project name
          baseName = project.name
            .replace(/\s*[-\s]*\s*CAM\s*\d+\s*$/i, "")
            .trim();

          // Remove any trailing dash or hyphen
          baseName = baseName.replace(/[-\s]+$/g, "").trim();

          cameraNumber = parseInt(camMatch[1], 10);
        }

        // Add camera number to the project data
        const projectWithCameraNumber = {
          ...project,
          cameraNumber,
        };

        // If this base name already exists in our grouped projects, add this camera to its cameras array
        if (groupedProjects[baseName]) {
          groupedProjects[baseName].cameras.push(projectWithCameraNumber);
        } else {
          // Otherwise, create a new entry with this camera as the first in the array
          groupedProjects[baseName] = {
            name: baseName,
            cameras: [projectWithCameraNumber],
          };
        }
      });

      // Sort cameras by camera number within each group
      Object.values(groupedProjects).forEach((project) => {
        if (project?.cameras?.length > 1) {
          project.cameras.sort((a, b) => {
            // If both have camera numbers, sort by number
            if (a?.cameraNumber != null && b?.cameraNumber != null) {
              return a.cameraNumber - b.cameraNumber;
            }
            // If only one has a camera number, put the one with a number first
            if (a?.cameraNumber != null) return -1;
            if (b?.cameraNumber != null) return 1;
            // If neither has a camera number, sort by name
            return (a?.name || "").localeCompare(b?.name || "");
          });
        }
      });

      // Convert the grouped projects object back to an array
      return Object.values(groupedProjects);
    };

    // Group active and completed projects
    activeProjects = groupCameras(activeProjects);
    completedProjects = groupCameras(completedProjects);

    // Sort the active projects by name alphabetically
    activeProjects.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    completedProjects.sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "")
    );

    return {
      props: {
        activeProjects,
        completedProjects,
        settings,
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error("Error in getStaticProps:", error);
    return {
      props: {
        activeProjects: [],
        completedProjects: [],
        settings: {},
      },
      revalidate: 60,
    };
  }
}
